import { useEffect, useState, useCallback } from 'react';
import { PENDING_KEY } from '@/components/ui/ContactButtons';
import type { Channel } from '@/components/ui/ContactButtons';

interface PendingLog {
  clientId: string;
  routeStepId?: string;
  channel: Channel;
  contactName?: string;
  timestamp: number;
}

const THIRTY_MINUTES = 30 * 60 * 1000;

function getPending(): PendingLog | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as PendingLog;
    if (Date.now() - data.timestamp > THIRTY_MINUTES) {
      sessionStorage.removeItem(PENDING_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

/**
 * Watches for the user returning to the app after tapping a contact button.
 * Returns the pending log data when the app regains focus within 30 min.
 */
export function useContactLog() {
  const [pending, setPending] = useState<PendingLog | null>(null);

  const check = useCallback(() => {
    const data = getPending();
    if (data) setPending(data);
  }, []);

  const dismiss = useCallback(() => {
    sessionStorage.removeItem(PENDING_KEY);
    setPending(null);
  }, []);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        check();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    // Also check on window focus (covers more edge cases on desktop)
    window.addEventListener('focus', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, [check]);

  return { pending, dismiss };
}
